import React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Participant } from '@/types/category';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Trash2, AlertTriangle } from 'lucide-react';
import { validateGroupMemberInRealTime } from '@/utils/nameValidationEnforcement';

interface ParticipantsEditorProps {
  participants: Participant[];
  onChange: (participants: Participant[]) => void;
}

const emptyParticipant: Participant = {
  firstName: '',
  lastName: '',
  preferredName: '',
  pronouns: '',
  consentToName: false,
  subgroup: '',
  roleTitle: '',
  likes: [],
  locationTag: '',
  hopeForSession: '',
  personalChallenge: '',
  meditationExperience: '',
  connectionStyle: '',
  backgroundStory: ''
};

const ParticipantsEditor: React.FC<ParticipantsEditorProps> = ({ participants, onChange }) => {
  const update = (idx: number, patch: Partial<Participant>) => {
    const next = participants.map((p, i) => (i === idx ? { ...p, ...patch } : p));
    
    // Real-time validation for name fields
    if ('firstName' in patch || 'preferredName' in patch) {
      const updatedParticipant = next[idx];
      const validation = validateGroupMemberInRealTime(updatedParticipant);
      if (!validation.isValid) {
        console.warn('🚨 [REAL-TIME VALIDATION] Group member name validation failed:', validation.errors);
      }
    }
    
    onChange(next);
  };

  const add = () => onChange([...(participants || []), { ...emptyParticipant }]);
  const remove = (idx: number) => onChange(participants.filter((_, i) => i !== idx));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label className="text-base">Participants (optional)</Label>
        <Button type="button" variant="secondary" onClick={add}>Add participant</Button>
      </div>

      {(participants || []).map((p, idx) => {
        const validation = validateGroupMemberInRealTime(p);
        const hasValidationErrors = !validation.isValid;
        
        return (
        <Card key={idx} className={`relative ${hasValidationErrors ? 'border-destructive' : ''}`}>
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                Participant {idx + 1}
                {hasValidationErrors && (
                  <AlertTriangle className="h-4 w-4 text-destructive" />
                )}
              </CardTitle>
              <Button
                type="button"
                variant="destructive"
                size="sm"
                onClick={() => remove(idx)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
            {hasValidationErrors && (
              <div className="text-sm text-destructive bg-destructive/10 p-2 rounded">
                <div className="font-medium">Name Validation Issues:</div>
                <ul className="list-disc list-inside mt-1">
                  {validation.errors.map((error, i) => (
                    <li key={i}>{error}</li>
                  ))}
                </ul>
              </div>
            )}
            {validation.warnings.length > 0 && (
              <div className="text-sm text-orange-600 bg-orange-50 p-2 rounded">
                <div className="font-medium">Suggestions:</div>
                <ul className="list-disc list-inside mt-1">
                  {validation.warnings.map((warning, i) => (
                    <li key={i}>{warning}</li>
                  ))}
                </ul>
              </div>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-1">
              <Label>First name</Label>
              <Input value={p.firstName} onChange={e => update(idx, { firstName: e.target.value })} placeholder="e.g., Ava" />
            </div>
            <div className="space-y-1">
              <Label>Last name (optional)</Label>
              <Input value={p.lastName || ''} onChange={e => update(idx, { lastName: e.target.value })} placeholder="e.g., Smith" />
            </div>
            <div className="space-y-1">
              <Label>Preferred name (optional)</Label>
              <Input value={p.preferredName || ''} onChange={e => update(idx, { preferredName: e.target.value })} placeholder="e.g., Avi" />
            </div>
            <div className="space-y-1">
              <Label>Pronouns (optional)</Label>
              <Input value={p.pronouns || ''} onChange={e => update(idx, { pronouns: e.target.value })} placeholder="e.g., she/her" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-1">
              <Label>Department/Team (optional)</Label>
              <Input value={p.subgroup || ''} onChange={e => update(idx, { subgroup: e.target.value })} placeholder="e.g., Engineering, Marketing" />
            </div>
            <div className="space-y-1">
              <Label>Role title (optional)</Label>
              <Input value={p.roleTitle || ''} onChange={e => update(idx, { roleTitle: e.target.value })} placeholder="e.g., PM" />
            </div>
            <div className="space-y-1">
              <Label>Location tag (optional)</Label>
              <Input value={p.locationTag || ''} onChange={e => update(idx, { locationTag: e.target.value })} placeholder="e.g., Minneapolis" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label>Likes (comma-separated, optional)</Label>
              <Input
                value={(p.likes || []).join(', ')}
                onChange={e => update(idx, { likes: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
                placeholder="e.g., runner, early mornings"
              />
            </div>
            <div className="space-y-1">
              <Label>Hope for session (≤120 chars, optional)</Label>
              <Textarea
                value={p.hopeForSession || ''}
                onChange={e => update(idx, { hopeForSession: e.target.value.substring(0, 120) })}
                rows={2}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label>Personal challenge (optional)</Label>
              <Input
                value={p.personalChallenge || ''}
                onChange={e => update(idx, { personalChallenge: e.target.value })}
                placeholder="e.g., dealing with work stress"
              />
            </div>
            <div className="space-y-1">
              <Label>Meditation experience (optional)</Label>
              <Input
                value={p.meditationExperience || ''}
                onChange={e => update(idx, { meditationExperience: e.target.value })}
                placeholder="e.g., beginner, experienced"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label>Connection style (optional)</Label>
              <Input
                value={p.connectionStyle || ''}
                onChange={e => update(idx, { connectionStyle: e.target.value })}
                placeholder="e.g., prefers quiet reflection"
              />
            </div>
            <div className="space-y-1">
              <Label>Background story (≤150 chars, optional)</Label>
              <Textarea
                value={p.backgroundStory || ''}
                onChange={e => update(idx, { backgroundStory: e.target.value.substring(0, 150) })}
                rows={2}
                placeholder="Brief personal context"
              />
            </div>
          </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Switch checked={!!p.consentToName} onCheckedChange={v => update(idx, { consentToName: v })} />
                <Label>Consent to name usage</Label>
              </div>
            </div>
          </CardContent>
        </Card>
        );
      })}
    </div>
  );
};

export default ParticipantsEditor;
